import {
  IBaseClinicalTrial,
  IBaseTrustTime,
  IChiCTRClinicalDetail as IChiCTRClinicalDetailCommon,
  ICTGOVClinicalDetail as ICTGOVClinicalDetailCommon,
  ICTRClinicalDetail as ICTRClinicalDetailCommon,
  IWHOClinicalDetail as IWHOClinicalDetailCommon,
  ICTISClinicalDetail as ICTISClinicalDetailCommon,
  IEUCTRClinicalDetail as IEUCTRClinicalDetailCommon,
} from '@patsnap/synapse_common_interface'
import { IBaseCountry, IBaseDictItem, IBaseOrgInfo, IDecryptIconFontData } from './base'
import { E_LEAD_OR_COLLABORATOR_TYPE } from './enum'
export interface ICTFacilityInfo {
  status?: string
  name?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  contacts?: {
    type?: string
    name?: string
  }[]
}
export interface IGroupIdView {
  clinical_trial_id: string
  group_id: string
  url: string
}

export interface ICTCountry {
  country_code: string
  name: string
  removed?: string
}

export interface ICTArmIntervention {
  design_group_type: string
  design_group_title: string
  design_group_description: string
  intervention?: ICTGOVArmInterventionIntervention[]
}

export interface ICTGOVArmInterventionIntervention {
  intervention_name: string
  intervention_description: string
  description?: string
  intervention_other_name: string[]
  intervention_type: string
  intervention_name_cn?: string
  trade_name?: string
}

export type ICTGOVArmInterventionForTable = ICTGOVArmInterventionIntervention & Omit<ICTArmIntervention, 'intervention'>

export interface ICTRecruitmentStatus extends IDecryptIconFontData {
  name: string
  normalized_status: string
}

export interface ICTCentralContact {
  contact_type?: string
  name?: string
  // CTR
  mobile?: string
  // CTGOV
  phone?: string
  email?: string
  post?: string
  telephone?: string
  address?: string
}

export type ICTSponsor = {
  name: string
  lead_or_collaborator?: E_LEAD_OR_COLLABORATOR_TYPE
  normalized_id?: string
  normalized_id_view?: IBaseOrgInfo
  sponsor_type?: 'primary' | 'secondary'
}

export type ICTGenderType = 'All' | 'Female' | 'Male'

export type ICTHealthyType = 'Accepts Healthy Volunteers' | 'No'

export interface ICTEligibility {
  gender?: ICTGenderType
  sampling_method?: string
  healthy_volunteers?: ICTHealthyType
  population?: string
  criteria?: string
  inclusion?: string[]
  exclusion?: string[]
  inclusion_criteria?: string
  exclusion_criteria?: string
}

export interface IClinicalSecondaryIdItem {
  id_field: string
  id_value: string[]
}
export interface ICTGOVGroupNormalizedSecondaryID {
  id_type: string
  id_value: string
  url: string
}

export interface ICTGOVIntervention {
  id: string
  name: string
  other_names: string[]
  type: string
  description: string
  normalized_id?: string
}

export interface ICTPhase {
  name: string
  normalized_phase: string
  normalized_phase_highest: string
  normalized_phase_view?: IBaseDictItem
  normalized_phase_highest_view: IBaseDictItem
}

export interface ICTPrimaryCompletionDate {
  completion_date: IBaseTrustTime
  date_type: string
}

export interface ICTGOVStudyType {
  name: string
  normalized_study_type: string
}

export interface IClinicalApprovalTimelineItem {
  country: string
  country_view: IBaseCountry
  dev_status_time: IBaseTrustTime
}

interface ICompletionDate {
  completion_date_type: string
  completion_date: IBaseTrustTime
}

interface ICTGOVEnrollment extends IDecryptIconFontData {
  enrollment?: string
  enrollment_type: string
}

interface ICTMaxAge {
  max_age: number
  max_age_unit: string
  max_age_text?: string
}

interface ICTMinAge {
  min_age: number
  min_age_unit: string
  min_age_text?: string
}

export interface ICTIdentifer {
  id_value?: string
  url?: string
}

interface ICTGOVStudyDesign {
  allocation: string
  intervention_model: string
  masking: string
  primary_purpose: string
}

interface ICTRStudyDesign {
  intervention_model?: string
  allocation?: string
  masking?: string
  study_category?: string
  location?: string
}

interface IWHOStudyDesign {
  intervention_model?: string
  allocation?: string
  masking?: string
}

export interface ICTOutcome {
  title: string
  outcome_type: 'Primary' | 'Secondary'
  endpoint_type: string
  time_frame: string
  description: string
}

export interface ICTRCentralInvestigator {
  name?: string
  degree?: string
  job_title?: string
  telephone?: string
  email?: string
  address?: string
  post?: string
  affiliation?: string
  role?: string
}

export interface ICTREnrollmentV2 extends IDecryptIconFontData {
  // 没有做标准化的原始值
  actual_text?: string
  already_text?: string
  estimated_text?: string
  // 国内国外
  location?: 'Domestic' | 'International'
}

export interface IWHOEnrollmentV2 {
  enrollment?: string
  enrollment_type?: string
}

export interface ICTCompletionDate {
  location: 'Domestic' | 'International'
  // 第一例受试者签署知情同意书日期
  first_sign_date: IBaseTrustTime
  // 第一例受试者入组日期
  first_participant_date: IBaseTrustTime
  // 试验暂停日期
  suspension_date?: IBaseTrustTime
  // 试验完成日期
  completion_date?: IBaseTrustTime
  // 试验终止日期
  termination_date?: IBaseTrustTime
}

export interface IWHOCompletionDate {
  // 试验完成日期
  completion_date?: IBaseTrustTime
  date_type?: string
}

export type IClinicalTranslationField =
  | 'brief_title'
  | 'official_title'
  | 'brief_summary'
  | 'study_purpose'
  | 'study_purpose'
  | 'detailed_description'
interface ICTDate {
  date: IBaseTrustTime
  date_type: string
}
export interface IClinicalTrial extends IBaseClinicalTrial, IDecryptIconFontData {}

export interface IClinicalHihFundedResearchIdView {
  funded_research_id: string
  project_id: string
  source: string
  source_url: string
}
export interface ICTGOVClinicalDetail extends ICTGOVClinicalDetailCommon, IDecryptIconFontData {}

export interface IEthicCommitteeResult {
  index: number // 该评审结果的编号
  name: string // 评审委员会的名称
  approval_date?: IBaseTrustTime // 评审的时间
  review_conclusion: string // 评审的结论
}

export interface ICTRClinicalDetail extends ICTRClinicalDetailCommon, IDecryptIconFontData {}

export interface IChiCTRClinicalDetail extends IChiCTRClinicalDetailCommon, IDecryptIconFontData {}

export interface IWHOClinicalDetail extends IWHOClinicalDetailCommon, IDecryptIconFontData {}

export interface ICTISClinicalDetail extends ICTISClinicalDetailCommon, IDecryptIconFontData {}

// EUCTR试验用药相关接口
export interface IEUCTRImp {
  form?: string
  role?: string
  route?: string
  trade_name?: string
  product_name?: string
  if_marketing_authorisation?: boolean
  if_specific_paediatric_formulation?: boolean
  if_orphan_drug?: boolean
  marketing_authorisation_holder?: string
  imp_detail?: Array<{
    inn_name?: string
  }>
}

export interface IEUCTRPlacebo {
  index: string
  if_placebo: boolean
  form: string
  route: string
}

export interface IEUCTRTrialMedication {
  imp: IEUCTRImp[]
  placebo: IEUCTRPlacebo[]
}

export interface IEUCTRClinicalDetail extends IEUCTRClinicalDetailCommon, IDecryptIconFontData {
  // EUCTR试验用药相关字段
  imp?: IEUCTRImp[]
  placebo?: IEUCTRPlacebo[]
}

export type IClinicalDetail =
  | ICTGOVClinicalDetail
  | ICTRClinicalDetail
  | IWHOClinicalDetail
  | IChiCTRClinicalDetail
  | ICTISClinicalDetail
  | IEUCTRClinicalDetail

export const isCtr = (data: IClinicalDetail): data is ICTRClinicalDetail => {
  return data.data_source === 'CTR'
}

export const isChictr = (data: IClinicalDetail): data is IChiCTRClinicalDetail => {
  return data.data_source === 'ChiCTR'
}

export const isCtis = (data: IClinicalDetail): data is ICTISClinicalDetail => {
  return data.data_source === 'CTIS'
}

export const isEuctr = (data: IClinicalDetail): data is IEUCTRClinicalDetail => {
  return data.data_source === 'EUCTR'
}
